import type { components } from '../api/schema.gen';

// Keyed by (vocabulary, value), never by value alone: `failed` means "attempts exhausted" on an event but "this attempt failed, more follow" on an attempt.

export type TEventStatus = components['schemas']['EEventStatus'];
export type TAttemptState = components['schemas']['AttemptResDto']['state'];
export type TIssueState = components['schemas']['IssueResDto']['state'];
export type TIssueErrorClass = components['schemas']['IssueResDto']['errorClass'];
export type TSourceState = components['schemas']['SourceResDto']['state'];
export type TSecretState = components['schemas']['SourceSecretResDto']['state'];
export type TDestinationState = components['schemas']['DestinationResDto']['state'];

export type TStatusGroup =
  'success' | 'motion' | 'retrying' | 'failure' | 'suppressed' | 'indeterminate';

export interface IStatusMeta {
  group: TStatusGroup;
  label: string;
  unreachable?: boolean;
  meaning?: string;
}

const EVENT: Record<TEventStatus, IStatusMeta> = {
  successful: { group: 'success', label: 'Delivered' },
  queued: { group: 'motion', label: 'Queued' },
  delivering: { group: 'motion', label: 'Delivering' },
  pending: {
    group: 'retrying',
    label: 'Retry pending',
    meaning: 'A delivery failed and the next attempt is already scheduled.',
  },
  on_hold: {
    group: 'suppressed',
    label: 'Held · disabled',
    meaning: 'Held until the CLI agent for this destination connects; then it goes out.',
  },
  failed: {
    group: 'failure',
    label: 'Retries spent',
    meaning:
      'Every attempt the policy allowed has been made. Nothing more happens on its own — retry it by hand.',
  },
  error: {
    group: 'failure',
    label: 'Failed before sending',
    meaning:
      'A rule or the transform threw before delivery. It never went out and never will on its own.',
  },
  filtered: {
    group: 'suppressed',
    label: 'Filtered by rule',
    meaning: 'A filter step said not to deliver it. Not a failure.',
  },
  dropped: {
    group: 'suppressed',
    label: 'Duplicate',
    meaning:
      'The deduplicate rule had already seen this one. The row exists so the question "where did my webhook go" has an answer.',
  },
  paused: {
    group: 'suppressed',
    label: 'Held · paused',
    meaning:
      'The flow is paused, so nothing is claimed for delivery. It keeps its place and its retry schedule, and goes out when you resume.',
  },
};

const ATTEMPT: Record<TAttemptState, IStatusMeta> = {
  scheduled: { group: 'motion', label: 'Scheduled' },
  in_flight: { group: 'motion', label: 'In flight' },
  succeeded: { group: 'success', label: 'Succeeded' },
  failed: {
    group: 'failure',
    label: 'Attempt failed',
    meaning: 'This one attempt failed. More may follow — the event is not finished.',
  },
  unknown: {
    group: 'indeterminate',
    label: 'Unknown',
    meaning:
      'Cut off before an answer came back. It may well have arrived — this is not a failure.',
  },
  abandoned: {
    group: 'suppressed',
    label: 'Abandoned',
    meaning: 'Given up on without a verdict — the flow was disabled or the event was superseded.',
  },
};

const ISSUE: Record<TIssueState, IStatusMeta> = {
  open: { group: 'failure', label: 'Open' },
  acknowledged: {
    group: 'motion',
    label: 'Acknowledged',
    meaning: 'Somebody has seen it. It is not fixed, and it stays in the active view.',
  },
  resolved: {
    group: 'suppressed',
    label: 'Resolved',
    meaning: 'Closed. Not necessarily fixed — closed is where we stopped counting.',
  },
  ignored: { group: 'suppressed', label: 'Ignored' },
};

const SOURCE: Record<TSourceState, IStatusMeta> = {
  active: {
    group: 'success',
    label: 'Receiving',
    meaning: 'Requests to this address are accepted.',
  },
  disabled: {
    group: 'suppressed',
    label: 'Refusing · 410',
    meaning:
      'Answers 410 Gone. Senders may switch the webhook off for good — this is not a pause, and nothing is queued.',
  },
};

const SECRET: Record<TSecretState, IStatusMeta> = {
  active: { group: 'success', label: 'Active' },
  grace: {
    group: 'motion',
    label: 'In overlap',
    meaning: 'Still accepted, until it expires. This is the window that makes a rotation seamless.',
  },
  expired: {
    group: 'suppressed',
    label: 'Expired',
    meaning: 'No longer checked. Its overlap window after a rotation ran out.',
  },
  revoked: {
    group: 'suppressed',
    label: 'Revoked',
    meaning: 'No longer checked. Somebody withdrew it by hand, before any clock ran out.',
  },
};

// Thresholds live on the server (architecture §4.2.1); these words only name its verdict.
const DESTINATION: Record<TDestinationState, IStatusMeta> = {
  unused: {
    group: 'suppressed',
    label: 'Unused',
    meaning: 'No live flow delivers here. A disabled flow sends nothing.',
  },
  disconnected: {
    group: 'suppressed',
    label: 'No agent connected',
    meaning: 'Nobody is listening, so events are held here until an agent connects.',
  },
  not_counted: {
    group: 'indeterminate',
    label: 'Not counted yet',
    meaning: 'The traffic figures have not been computed yet. Unknown, not healthy.',
  },
  idle: {
    group: 'suppressed',
    label: 'Idle',
    meaning: 'Nothing was attempted here in the counting window, so there is no verdict.',
  },
  healthy: {
    group: 'success',
    label: 'Healthy',
    meaning: 'Under 5% of the events attempted in the window failed or wait on a retry.',
  },
  degraded: {
    group: 'motion',
    label: 'Degraded',
    meaning: '5% or more of the events attempted in the window failed or wait on a retry.',
  },
  failing: {
    group: 'failure',
    label: 'Failing',
    meaning: 'Half or more failed, or the last five settled events all did.',
  },
};

export interface IErrorClassMeta {
  label: string;
  answered: boolean;
  reached: boolean;
  instead: string;
}

const ERROR_CLASS: Record<TIssueErrorClass, IErrorClassMeta> = {
  status_5xx_gateway: { label: 'Gateway error', answered: true, reached: true, instead: '' },
  status_500: { label: 'Server error', answered: true, reached: true, instead: '' },
  status_429: { label: 'Rate limited', answered: true, reached: true, instead: '' },
  status_4xx: {
    label: 'Rejected by the destination',
    answered: true,
    reached: true,
    instead: '',
  },
  auth_failed: {
    label: 'Sender authentication failed',
    answered: false,
    reached: false,
    instead: 'failed on the way in — no delivery was attempted',
  },
  transform_error: {
    label: 'Transform threw',
    answered: false,
    reached: false,
    instead: 'the transform threw before anything was sent',
  },
  config_missing: {
    label: 'Configuration missing',
    answered: false,
    reached: false,
    instead: 'nothing to send to — the step names no destination that exists',
  },
  broker_unavailable: {
    label: 'Queue unavailable',
    answered: false,
    reached: false,
    instead: 'the queue never accepted it',
  },
  conn_failed: {
    label: 'Could not connect',
    answered: false,
    reached: true,
    instead: 'no connection, so no answer',
  },
  timeout: {
    label: 'No answer before the deadline',
    answered: false,
    reached: true,
    instead: 'the deadline expired before an answer',
  },
  unknown: {
    label: 'Unclassified',
    answered: true,
    reached: true,
    instead: 'no answer was recorded',
  },
  node_error: {
    label: 'A step failed',
    answered: false,
    reached: false,
    instead: 'a step of the flow failed before anything was sent',
  },
  node_unavailable: {
    label: 'Step not available',
    answered: false,
    reached: false,
    instead: 'this server cannot run that kind of step',
  },
  run_limit: {
    label: 'Run limit reached',
    answered: false,
    reached: false,
    instead: 'the run made more copies or steps than a run may',
  },
  join_timeout: {
    label: 'Stopped waiting',
    answered: false,
    reached: false,
    instead: 'a merge stopped waiting for the other branches',
  },
  schedule_skipping: {
    label: 'Schedule keeps skipping',
    answered: false,
    reached: false,
    instead: 'the last run is still going, so the schedule started none',
  },
  'ai.budget_exhausted': {
    label: 'AI budget exhausted',
    answered: false,
    reached: false,
    instead: 'the organization spent today’s AI budget, so events wait for it to reset',
  },
};

// An attempt's own classes, finer than an issue's: the same string never means two things.
const DELIVERY_ERROR_CLASS: Record<string, IErrorClassMeta> = {
  dns: {
    label: 'Name did not resolve',
    answered: false,
    reached: false,
    instead: 'the name did not resolve — nothing was sent',
  },
  conn_refused: {
    label: 'Connection refused',
    answered: false,
    reached: false,
    instead: 'the connection was refused — nothing was sent',
  },
  tls: {
    label: 'TLS handshake failed',
    answered: false,
    reached: false,
    instead: 'the handshake did not finish — nothing was sent',
  },
  reset: {
    label: 'Connection reset',
    answered: false,
    reached: true,
    instead: 'the connection dropped before an answer',
  },
  redirect: { label: 'Redirected, not followed', answered: true, reached: true, instead: '' },
  response_too_large: { label: 'Answer too large', answered: true, reached: true, instead: '' },
  status_5xx: { label: 'Destination error · 5xx', answered: true, reached: true, instead: '' },
  invalid_header_value: {
    label: 'Header we could not send',
    answered: false,
    reached: false,
    instead: 'a header value was invalid — nothing was sent',
  },
  cli_local_error: {
    label: 'Local server unreachable',
    answered: false,
    reached: true,
    instead: 'the agent could not reach the local server',
  },
  cli_resend_exhausted: {
    label: 'Agent never acknowledged',
    answered: false,
    reached: true,
    instead: 'the agent never acknowledged it',
  },
  cli_hold_expired: {
    label: 'Held too long',
    answered: false,
    reached: false,
    instead: 'no agent connected before the hold ran out',
  },
  unroutable_transport: {
    label: 'Transport not available',
    answered: false,
    reached: false,
    instead: 'this server has no such transport',
  },
};

export const errorClassMeta = (value: string): IErrorClassMeta | null =>
  (ERROR_CLASS as Record<string, IErrorClassMeta | undefined>)[value] ??
  DELIVERY_ERROR_CLASS[value] ??
  null;

export const httpCodeGroup = (code: number): TStatusGroup => {
  if (code >= 400) return 'failure';
  if (code >= 300) return 'motion';
  if (code >= 200) return 'success';
  return 'indeterminate';
};

export const GROUP_CLASS: Record<TStatusGroup, string> = {
  success: 'text-success',
  motion: 'text-motion',
  retrying: 'text-motion',
  failure: 'text-danger',
  suppressed: 'text-faint',
  indeterminate: 'text-motion',
};

export const GROUP_TEXT_CLASS: Record<TStatusGroup, string> = {
  success: 'text-primary',
  motion: 'text-primary',
  retrying: 'text-primary',
  failure: 'text-primary',
  suppressed: 'text-faint',
  indeterminate: 'text-primary',
};

const VOCABULARIES = {
  event: EVENT,
  attempt: ATTEMPT,
  issue: ISSUE,
  source: SOURCE,
  secret: SECRET,
  destination: DESTINATION,
} as const;

export type TVocabulary = keyof typeof VOCABULARIES;

// Null, never a nearest guess: falling back to `success` would report a delivery that never happened.
export const statusMeta = <V extends TVocabulary>(
  vocabulary: V,
  value: string,
): IStatusMeta | null => {
  const table = VOCABULARIES[vocabulary] as Record<string, IStatusMeta | undefined>;
  return table[value] ?? null;
};
